import type { NewRoleFormData } from "@/schemas/roles/new-roles";
import type { ApiRoleResponseType } from "@/types/entities/roles";
import axios from "@/utils/axios";
import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export interface GetRolesFilters {
  search?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  filter?: { [key: string]: string };
}

export const useGetRoles = (filters: GetRolesFilters, canLoad = true) => {
  const { page = 1, ...restParams } = filters || {};

  const queryParams = {
    ...restParams,
    page,
  };

  return useQuery({
    queryKey: ["roles", queryParams],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ApiRoleResponseType> => {
      const response = await axios.get("/admin/v1/roles", {
        params: queryParams,
      });
      return response.data;
    },
    enabled: canLoad, // Only run the query if canLoad is true
  });
};

export const useStoreRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: NewRoleFormData & {
        permissions: string[];
        users?: string[];
        type: string;
      },
    ) => {
      const response = await axios.post("/admin/v1/roles", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error creating role:", error);
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(`/admin/v1/roles/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error deleting role:", error);
    },
  });
};

export const getRole = async (
  roleId: string,
): Promise<{
  data: ApiRoleResponseType["data"][number];
}> => {
  try {
    const response = await axios.get(`/admin/v1/roles/${roleId}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching role:", e);
    throw e; // Re-throw the error to be handled by the query
  }
};

export const useGetRole = (roleId: string) => {
  return useQuery({
    queryKey: ["role", roleId],
    queryFn: async (): Promise<{
      data: ApiRoleResponseType["data"][number];
    }> => {
      return getRole(roleId);
    },
    networkMode: "always",
    enabled: !!roleId, // Only run the query if roleId is provided
  });
};

export const useUpdateRole = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      id: string;
      name: string;
      type: string;
      description?: string;
    }) => {
      const response = await axios.put(`/admin/v1/roles/${data.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["role", id],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error updating role:", error);
    },
  });
};

export const useDuplicateRole = (roleId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      description,
    }: {
      name: string;
      description: string;
    }) => {
      const response = await axios.post(`/admin/v1/roles/${roleId}/duplicate`, {
        name,
        description,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error duplicating role:", error);
    },
  });
};

export const useGetPermissions = () => {
  return useQuery({
    queryKey: ["permissions"],
    queryFn: async (): Promise<
      {
        id: string;
        name: string;
        display_name: string;
        group_name: string;
      }[]
    > => {
      try {
        const response = await axios.get("/admin/v1/permissions");
        return response.data.data;
      } catch (e) {
        console.error("Error fetching permissions:", e);
        throw e; // Re-throw the error to be handled by the query
      }
    },
  });
};

export const useGetRolePermissions = (roleId: string) => {
  return useQuery({
    queryKey: ["rolePermissions", roleId],
    queryFn: async (): Promise<
      {
        id: string;
        name: string;
        display_name: string;
      }[]
    > => {
      const response = await axios.get(`/admin/v1/roles/${roleId}/permissions`);
      return response.data.data;
    },
    enabled: !!roleId, // Only run the query if roleId is provided
  });
};

export const useUpdateRolePermissions = (roleId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (permissions: string[]) => {
      const response = await axios.put(
        `/admin/v1/roles/${roleId}/permissions`,
        {
          permissions,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the role permissions query to refresh the data
      queryClient.invalidateQueries({
        queryKey: ["rolePermissions", roleId],
      });
    },
  });
};

export const useGetRolesAsInfinite = (filters: GetRolesFilters) => {
  const queryParams = {
    per_page: filters.per_page || 10,
    ...(filters.search && { search: filters.search }),
    sort_by: filters.sort_by || "name",
    sort_direction: filters.sort_direction || "desc",
  };

  return useInfiniteQuery({
    queryKey: ["roles-infinite", queryParams],
    queryFn: async ({ pageParam = 1 }): Promise<ApiRoleResponseType> => {
      const response = await axios.get("/admin/v1/roles", {
        params: {
          ...queryParams,
          page: pageParam,
        },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (lastPage as ApiRoleResponseType)
        .meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
  });
};

export const createRoleDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    const roleId = id;
    try {
      const role = await queryClient.ensureQueryData({
        queryKey: ["role", roleId],
        queryFn: async () => getRole(roleId),
        staleTime: 5 * 60 * 1000, // 5 minutes
      });
      return { data: role.data };
    } catch (error) {
      console.log("Error fetching role for breadcrumb:", error);
      return {
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  };
};

export const useActivateRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.post(`/admin/v1/roles/${id}/activate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
    },
    onError: (error) => {
      console.error("Error activating role:", error);
    },
  });
};
